import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import type { NotificationDto } from 'api';

import { hasRoute } from '../notification-routing';
import { visualFor } from '../notification-meta';
import { hasCostUpdatedDetail } from '../cost-updated-payload';

/**
 * Render presentacional de una notificación dentro del dropdown de la campanita.
 * No conoce el store: emite `open` (click navegable) y `remove`; el contenedor decide qué hacer.
 */
@Component({
  selector: 'app-notification-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="flex items-start gap-3 p-3 rounded-lg transition-colors"
      [class.cursor-pointer]="navigable()"
      [class.hover:bg-surface-100]="navigable()"
      [class.dark:hover:bg-surface-800]="navigable()"
      [class.bg-primary-50]="!notification().isRead"
      [class.dark:bg-primary-950]="!notification().isRead"
      role="listitem"
      [attr.tabindex]="navigable() ? 0 : null"
      (click)="onOpen()"
      (keydown.enter)="onOpen()"
    >
      <i [class]="visual().icon + ' ' + visual().colorClass" class="text-lg mt-0.5 flex-shrink-0"></i>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          @if (!notification().isRead) {
            <span class="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" aria-label="No leída"></span>
          }
          <p class="font-medium text-sm truncate" [class.font-semibold]="!notification().isRead">
            {{ notification().title }}
          </p>
        </div>
        @if (bodyText()) {
          <p class="text-sm text-surface-600 dark:text-surface-300 mt-0.5 line-clamp-2">{{ bodyText() }}</p>
        }
        <span class="text-xs text-surface-400 mt-1 block">{{ relativeTime() }}</span>
      </div>

      @if (hasDetail()) {
        <button
          type="button"
          class="flex-shrink-0 text-surface-400 hover:text-primary-500 transition-colors p-1"
          title="Ver desglose por lista"
          aria-label="Ver desglose por lista"
          (click)="onDetail($event)"
        >
          <i class="pi pi-table text-xs"></i>
        </button>
      }

      <button
        type="button"
        class="flex-shrink-0 text-surface-400 hover:text-red-500 transition-colors p-1"
        title="Eliminar notificación"
        aria-label="Eliminar notificación"
        (click)="onRemove($event)"
      >
        <i class="pi pi-times text-xs"></i>
      </button>
    </div>
  `,
})
export class NotificationItemComponent {
  readonly notification = input.required<NotificationDto>();

  /** Click sobre la notificación (para navegar y marcar leída). */
  readonly open = output<NotificationDto>();
  /** Click sobre el botón eliminar. */
  readonly remove = output<NotificationDto>();
  /** Click sobre "ver desglose" (solo notificaciones de costo con `lists[]`). */
  readonly detail = output<NotificationDto>();

  readonly visual = computed(() => visualFor(this.notification()));
  readonly navigable = computed(() => hasRoute(this.notification()));
  readonly relativeTime = computed(() => formatRelativeTime(this.notification().createdAt));
  /** `true` si la notificación tiene desglose de costo (`payloadJson` con `lists[]`). */
  readonly hasDetail = computed(() => hasCostUpdatedDetail(this.notification().payloadJson));
  /** Cuerpo saneado: el backend a veces incrusta ids internos (GUIDs) en el texto; no los mostramos. */
  readonly bodyText = computed(() => stripEntityIds(this.notification().body));

  onOpen(): void {
    this.open.emit(this.notification());
  }

  onRemove(event: Event): void {
    event.stopPropagation();
    this.remove.emit(this.notification());
  }

  onDetail(event: Event): void {
    event.stopPropagation();
    this.detail.emit(this.notification());
  }
}

/** GUID canónico (8-4-4-4-12 hex). No colisiona con números de documento como `REC-000123`. */
const GUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

/**
 * Quita ids internos (GUIDs) del `body` para que el usuario no los vea. Desde la guía 2026-07-02 el
 * backend garantiza que `title`/`body` no traen GUIDs; esto queda como defensa barata ante regresiones
 * y para notificaciones antiguas ya persistidas. Limpia además los paréntesis y espacios que queden.
 * No toca códigos de documento legibles (`REC-000123`, `F-001`), que no tienen formato GUID.
 */
export function stripEntityIds(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(GUID_RE, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:])/g, '$1')
    .trim();
}

const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });

/** Formatea una fecha ISO como tiempo relativo en español (ej. "hace 5 minutos"). */
export function formatRelativeTime(iso: string | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const diffSeconds = Math.round((then - now) / 1000);
  const abs = Math.abs(diffSeconds);

  if (abs < 60) return rtf.format(Math.round(diffSeconds), 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSeconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSeconds / 3600), 'hour');
  if (abs < 2592000) return rtf.format(Math.round(diffSeconds / 86400), 'day');
  if (abs < 31536000) return rtf.format(Math.round(diffSeconds / 2592000), 'month');
  return rtf.format(Math.round(diffSeconds / 31536000), 'year');
}
